import React, { useState } from 'react';
import {
  Badge, Box, Button, HStack, Text, useToast,
} from '@chakra-ui/react';
import { FiExternalLink, FiRefreshCw, FiEyeOff } from 'react-icons/fi';
import { useSalesOrderPortal, useSalesOrderPortalActions, SalesOrderShareRow } from '../hooks/useSalesOrderPortal';
import { SqlDateTimeToDisplay } from '../utils/SqlTools';

const money = (n: number) => `$${(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const SCHEME: Record<SalesOrderShareRow['status'], string> = {
  Sent: 'orange',
  Approved: 'green',
  Declined: 'red',
  Superseded: 'gray',
  Converted: 'blue',
  Complete: 'green',
  Withdrawn: 'gray',
};

const LABEL: Record<SalesOrderShareRow['status'], string> = {
  Sent: 'AWAITING CUSTOMER',
  Approved: 'CUSTOMER APPROVED',
  Declined: 'CUSTOMER DECLINED',
  Superseded: 'SUPERSEDED',
  Converted: 'CONVERTED TO INVOICE',
  Complete: 'COMPLETE',
  Withdrawn: 'WITHDRAWN',
};

// item 1402 (Chad) — the sales order's CUSTOMER PORTAL panel. Sharing mints a frozen copy (header
// + the branded PDF) that the portal serves; emailing the sales order does the same in one motion.
// Re-sharing after an edit supersedes the earlier copy so the customer always approves exactly
// what they saw. Withdraw pulls it back. Work orders never get this panel — the server refuses them.
const SalesOrderPortalPanel: React.FC<{ workOrderId: string; disabled?: boolean }> = ({ workOrderId, disabled }) => {
  const toast = useToast();
  const { data, isLoading } = useSalesOrderPortal(workOrderId);
  const { share, withdraw } = useSalesOrderPortalActions(workOrderId);
  const [busy, setBusy] = useState<'share' | 'withdraw' | null>(null);

  const active = data?.active ?? null;
  const canWithdraw = !!active && (active.status === 'Sent' || active.status === 'Approved' || active.status === 'Declined');
  const converted = !!active && (active.status === 'Converted' || active.status === 'Complete');

  const onShare = async () => {
    setBusy('share');
    try {
      const row = await share();
      toast({ status: 'success', title: active ? 'Re-shared to the customer portal' : 'Shared to the customer portal', description: `${row.number} is now visible to the customer for approval.` });
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not share', description: String(e?.response?.data ?? e?.message ?? 'Please try again.'), duration: 8000, isClosable: true,
      });
    } finally {
      setBusy(null);
    }
  };

  const onWithdraw = async () => {
    setBusy('withdraw');
    try {
      await withdraw();
      toast({ status: 'info', title: 'Withdrawn from the customer portal' });
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not withdraw', description: String(e?.response?.data ?? e?.message ?? 'Please try again.'), duration: 8000, isClosable: true,
      });
    } finally {
      setBusy(null);
    }
  };

  let detail: React.ReactNode = null;
  if (active) {
    if (active.status === 'Sent') detail = `Shared ${SqlDateTimeToDisplay(active.sentDate)} for ${money(active.total)} — waiting on the customer.`;
    else if (active.status === 'Approved') detail = `Approved by ${active.approvedByName} on ${SqlDateTimeToDisplay(active.approvedDate ?? '')} — convert it to an invoice.`;
    else if (active.status === 'Declined') detail = `Declined by ${active.declinedByName} on ${SqlDateTimeToDisplay(active.declinedDate ?? '')}${active.declineReason ? ` — “${active.declineReason}”` : ''}.`;
    else if (active.status === 'Converted') detail = 'Converted — the customer now sees the invoice in their portal.';
    else if (active.status === 'Complete') detail = 'Complete — fully invoiced.';
  }

  return (
    <Box>
      <HStack spacing={3} align="center" mb={2} flexWrap="wrap">
        {active ? (
          <Badge colorScheme={SCHEME[active.status]} borderRadius="full" px={2}>{LABEL[active.status]}</Badge>
        ) : (
          <Badge colorScheme="gray" borderRadius="full" px={2}>NOT SHARED</Badge>
        )}
        {detail && <Text fontSize="sm" opacity={0.8}>{detail}</Text>}
        {!active && !isLoading && (
          <Text fontSize="sm" opacity={0.8}>
            The customer can&apos;t see this sales order yet. Emailing it shares it automatically, or share it here.
          </Text>
        )}
      </HStack>
      <HStack spacing={2}>
        {!converted && (
          <Button
            size="sm"
            variant="outline"
            leftIcon={active ? <FiRefreshCw /> : <FiExternalLink />}
            isLoading={busy === 'share'}
            isDisabled={disabled || busy !== null}
            onClick={onShare}
          >
            {active ? 'Re-share to customer portal' : 'Share to customer portal'}
          </Button>
        )}
        {canWithdraw && (
          <Button
            size="sm"
            variant="ghost"
            leftIcon={<FiEyeOff />}
            isLoading={busy === 'withdraw'}
            isDisabled={busy !== null}
            onClick={onWithdraw}
          >
            Withdraw from portal
          </Button>
        )}
      </HStack>
      {active && active.status !== 'Converted' && active.status !== 'Complete' && (
        <Text fontSize="xs" opacity={0.6} mt={2}>
          Re-sharing after an edit replaces the customer&apos;s copy with a fresh one; the earlier copy is kept as history.
        </Text>
      )}
    </Box>
  );
};

SalesOrderPortalPanel.defaultProps = { disabled: false };

// item 1402 — the same share, as a footer action beside Send / PDF (discoverability: Chad looked
// for it at the bottom of the form first). Label follows the live state.
export const SalesOrderShareButton: React.FC<{ workOrderId: string }> = ({ workOrderId }) => {
  const toast = useToast();
  const { data } = useSalesOrderPortal(workOrderId);
  const { share } = useSalesOrderPortalActions(workOrderId);
  const [busy, setBusy] = useState(false);
  const active = data?.active ?? null;
  if (active && (active.status === 'Converted' || active.status === 'Complete')) return null;
  const onClick = async () => {
    setBusy(true);
    try {
      const row = await share();
      toast({ status: 'success', title: active ? 'Re-shared to the customer portal' : 'Shared to the customer portal', description: `${row.number} is now visible to the customer for approval.` });
    } catch (e: any) {
      toast({
        status: 'error', title: 'Could not share', description: String(e?.response?.data ?? e?.message ?? 'Please try again.'), duration: 8000, isClosable: true,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Button size="sm" variant="ghost" leftIcon={active ? <FiRefreshCw /> : <FiExternalLink />} isLoading={busy} onClick={onClick}>
      {active ? 'Re-share to portal' : 'Share to customer portal'}
    </Button>
  );
};

export default SalesOrderPortalPanel;
